import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Plug, RefreshCw, Trash2 } from "lucide-react";
import {
  auth as authApi,
  household as householdApi,
  connections as connectionsApi,
  reports as reportsApi,
  type CurrentUser,
  type SocialConnection,
  type SocialProvider,
  type ConnectionStatus,
  type ConnectProvider,
} from "@/api/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/AuthContext";
import { can } from "@/lib/permissions";
import { fmtDate } from "@/lib/content";
import { cn } from "@/lib/utils";
import { ImportPanel } from "@/components/ImportPanel";
import { ThemeSwitch } from "@/components/ThemeSwitch";
import { PageHeading } from "@/components/PageHeading";

export function SettingsPage() {
  const { user } = useAuth();

  if (!user) {
    return <div className="text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <PageHeading
        title="Settings"
        sub="Your account, your workspace, and your password — all in one place."
        eyebrow="Kovach Consulting · Settings"
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Appearance</CardTitle>
          <CardDescription>
            Switch the look. Sparkle is a maximalist holographic theme; Steele is the default.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ThemeSwitch />
        </CardContent>
      </Card>

      <AccountSection user={user} />
      <WorkspaceSection user={user} />
      <ConnectionsSection />
      <PasswordSection />
    </div>
  );
}

function AccountSection({ user }: { user: CurrentUser }) {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState(user.display_name);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setDisplayName(user.display_name); }, [user.display_name]);

  const save = useMutation({
    mutationFn: () => authApi.updateMe({ display_name: displayName.trim() }),
    onSuccess: (res) => {
      queryClient.setQueryData(["me"], res.user);
      setStatus("saved");
      setTimeout(() => setStatus("idle"), 2000);
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : "Couldn't save");
      setStatus("error");
    },
  });

  function submit() {
    if (status === "saving") return;
    if (displayName.trim() === "" || displayName.trim() === user.display_name) return;
    setStatus("saving");
    setError(null);
    save.mutate();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Account</CardTitle>
        <CardDescription>Your name and email.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="display-name">Your name</Label>
          <Input
            id="display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={100}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" value={user.email} disabled />
          <div className="text-[10px] text-muted-foreground">
            Email changes need re-verification — coming in a future update.
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="text-xs">
            {status === "saved" ? <span className="text-success">Saved.</span> : null}
            {status === "error" ? <span className="text-destructive">{error}</span> : null}
          </div>
          <Button
            size="sm"
            onClick={submit}
            disabled={
              status === "saving" ||
              displayName.trim() === "" ||
              displayName.trim() === user.display_name
            }
          >
            {status === "saving" ? "Saving…" : "Save name"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function WorkspaceSection({ user }: { user: CurrentUser }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(user.household_name);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(user.household_name);
  }, [user.household_name]);

  const isOwner = user.role === "owner";
  const dirty = name.trim() !== user.household_name;

  const save = useMutation({
    mutationFn: () => householdApi.update({ name: name.trim() }),
    onSuccess: (res) => {
      queryClient.setQueryData(["me"], res.user);
      setStatus("saved");
      setTimeout(() => setStatus("idle"), 2000);
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : "Couldn't save");
      setStatus("error");
    },
  });

  function submit() {
    if (!isOwner) return;
    if (status === "saving" || !dirty || name.trim() === "") return;
    setStatus("saving");
    setError(null);
    save.mutate();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Workspace</CardTitle>
        <CardDescription>
          The brand or business this plan belongs to. Shown in the sidebar.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="ws-name">Name</Label>
          <Input
            id="ws-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            disabled={!isOwner}
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="text-xs">
            {!isOwner ? <span className="text-muted-foreground">Owner only.</span> : null}
            {status === "saved" ? <span className="text-success">Saved.</span> : null}
            {status === "error" ? <span className="text-destructive">{error}</span> : null}
          </div>
          <Button
            size="sm"
            onClick={submit}
            disabled={status === "saving" || !dirty || name.trim() === "" || !isOwner}
          >
            {status === "saving" ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: () => authApi.changePassword(current, next),
    onSuccess: () => {
      setStatus("saved");
      setCurrent("");
      setNext("");
      setConfirm("");
      setTimeout(() => setStatus("idle"), 2500);
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : "Couldn't change password");
      setStatus("error");
    },
  });

  function submit() {
    if (status === "saving") return;
    if (next.length < 8) { setError("New password must be at least 8 characters"); setStatus("error"); return; }
    if (next !== confirm) { setError("Passwords don't match"); setStatus("error"); return; }
    setStatus("saving");
    setError(null);
    change.mutate();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Password</CardTitle>
        <CardDescription>Change your sign-in password.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="cur-pw">Current password</Label>
          <Input
            id="cur-pw"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="new-pw">New password</Label>
          <Input
            id="new-pw"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            minLength={8}
          />
          <div className="text-[10px] text-muted-foreground">At least 8 characters.</div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm-pw">Confirm new password</Label>
          <Input
            id="confirm-pw"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="text-xs">
            {status === "saved" ? <span className="text-success">Password updated.</span> : null}
            {status === "error" ? <span className="text-destructive">{error}</span> : null}
          </div>
          <Button
            size="sm"
            onClick={submit}
            disabled={status === "saving" || current === "" || next === "" || confirm === ""}
          >
            {status === "saving" ? "Saving…" : "Change password"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

const PROVIDER_LABEL: Record<SocialProvider, string> = {
  meta_facebook: "Facebook Page",
  meta_instagram: "Instagram",
  tiktok: "TikTok",
  manual_linkedin: "LinkedIn (manual)",
  manual_substack: "Substack (manual)",
};

const STATUS_BADGE: Record<
  ConnectionStatus,
  { variant: "success" | "destructive" | "muted" | "outline"; label: string }
> = {
  active: { variant: "success", label: "Active" },
  expired: { variant: "destructive", label: "Expired" },
  error: { variant: "destructive", label: "Error" },
  revoked: { variant: "muted", label: "Off" },
};

function ConnectionsSection() {
  const { user } = useAuth();
  const canEdit = can(user, "edit_operational");
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();

  const { data } = useQuery({
    queryKey: ["connections"],
    queryFn: () => connectionsApi.list(),
  });

  const connected = params.get("connected");
  const connectError = params.get("connect_error");
  const connectedCount = params.get("n");

  // Coming back from an OAuth round-trip: refresh the list + any open report.
  useEffect(() => {
    if (connected) {
      queryClient.invalidateQueries({ queryKey: ["connections"] });
      queryClient.invalidateQueries({ queryKey: ["reports"] });
    }
  }, [connected, queryClient]);

  const disconnectMut = useMutation({
    mutationFn: (id: number) => connectionsApi.disconnect(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connections"] });
      queryClient.invalidateQueries({ queryKey: ["reports"] });
    },
  });

  const syncMut = useMutation({
    mutationFn: () => reportsApi.sync(30),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["connections"] });
      queryClient.invalidateQueries({ queryKey: ["reports"] });
    },
  });

  function clearBanner() {
    params.delete("connected");
    params.delete("connect_error");
    params.delete("n");
    setParams(params, { replace: true });
  }

  function startConnect(provider: ConnectProvider) {
    window.location.href = connectionsApi.startUrl(provider);
  }

  const conns = data?.connections ?? [];
  const cfg = data?.configured;
  const noProvider = cfg ? !cfg.meta && !cfg.tiktok : false;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Connections</CardTitle>
        <CardDescription>
          Link Meta (Facebook + Instagram) and TikTok to pull engagement into Reports. Access
          tokens are encrypted at rest and never leave the server.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {connected ? (
          <Banner tone="success" onClose={clearBanner}>
            Connected {connected === "meta" ? "Meta" : "TikTok"}
            {connectedCount ? ` — ${connectedCount} account${connectedCount === "1" ? "" : "s"}` : ""}.
            {canEdit ? " Run a sync to pull the latest numbers." : ""}
          </Banner>
        ) : null}
        {connectError ? (
          <Banner tone="error" onClose={clearBanner}>
            Couldn&apos;t connect: {connectError}
          </Banner>
        ) : null}
        {cfg && !cfg.encryption ? (
          <Banner tone="warn">
            Token encryption isn&apos;t set up yet (TOKEN_ENCRYPTION_KEY). Connecting stays disabled
            until it&apos;s configured on the server.
          </Banner>
        ) : null}

        {canEdit ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={!cfg?.meta || !cfg?.encryption}
              onClick={() => startConnect("meta")}
            >
              <Plug size={14} className="mr-1.5" /> Connect Meta
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!cfg?.tiktok || !cfg?.encryption}
              onClick={() => startConnect("tiktok")}
            >
              <Plug size={14} className="mr-1.5" /> Connect TikTok
            </Button>
            {conns.length > 0 ? (
              <Button
                size="sm"
                variant="ghost"
                className="ml-auto"
                disabled={syncMut.isPending}
                onClick={() => syncMut.mutate()}
              >
                <RefreshCw size={14} className={cn("mr-1.5", syncMut.isPending && "animate-spin")} />
                {syncMut.isPending ? "Syncing…" : "Sync now"}
              </Button>
            ) : null}
          </div>
        ) : null}

        <ImportPanel canEdit={canEdit} />

        {cfg && (!cfg.meta || !cfg.tiktok) ? (
          <div className="text-xs text-muted-foreground">
            {noProvider
              ? "Neither provider is configured on the server yet."
              : !cfg.meta
                ? "Meta isn't configured on the server yet."
                : "TikTok isn't configured on the server yet."}{" "}
            See CONTENT_PLANNER_HANDOFF.md for the credentials to set.
          </div>
        ) : null}

        {conns.length === 0 ? (
          <div className="text-sm text-muted-foreground">No accounts connected yet.</div>
        ) : (
          <div className="divide-y border rounded-md">
            {conns.map((c) => (
              <ConnectionRow
                key={c.id}
                conn={c}
                canEdit={canEdit}
                disconnecting={disconnectMut.isPending}
                onDisconnect={() => {
                  if (
                    window.confirm(
                      `Disconnect ${PROVIDER_LABEL[c.provider]}${
                        c.external_name ? ` (${c.external_name})` : ""
                      }? This removes the connection and the data it pulled into Reports.`,
                    )
                  ) {
                    disconnectMut.mutate(c.id);
                  }
                }}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ConnectionRow({
  conn,
  canEdit,
  disconnecting,
  onDisconnect,
}: {
  conn: SocialConnection;
  canEdit: boolean;
  disconnecting: boolean;
  onDisconnect: () => void;
}) {
  const badge = STATUS_BADGE[conn.status];
  return (
    <div className="flex items-center gap-3 p-3">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">
          {conn.external_name || conn.username || PROVIDER_LABEL[conn.provider]}
        </div>
        <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2">
          <span>{PROVIDER_LABEL[conn.provider]}</span>
          {conn.username ? <span>@{conn.username}</span> : null}
          <span aria-hidden>·</span>
          <span>synced {conn.last_synced_at ? fmtDate(conn.last_synced_at) : "never"}</span>
        </div>
        {conn.status === "error" && conn.last_error ? (
          <div className="text-xs text-destructive mt-0.5 truncate" title={conn.last_error}>
            {conn.last_error}
          </div>
        ) : null}
        {conn.status === "expired" ? (
          <div className="text-xs text-destructive mt-0.5">Token expired — reconnect this account.</div>
        ) : null}
      </div>
      <Badge variant={badge.variant}>{badge.label}</Badge>
      {canEdit ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={onDisconnect}
          disabled={disconnecting}
          title="Disconnect"
        >
          <Trash2 size={14} />
        </Button>
      ) : null}
    </div>
  );
}

function Banner({
  tone,
  children,
  onClose,
}: {
  tone: "success" | "error" | "warn";
  children: ReactNode;
  onClose?: () => void;
}) {
  const cls =
    tone === "success"
      ? "border-l-success bg-success/5"
      : tone === "error"
        ? "border-l-destructive bg-destructive/5"
        : "border-l-accent bg-accent/5";
  return (
    <div className={cn("border-l-4 rounded-md px-3 py-2 text-sm flex items-start gap-2", cls)}>
      <div className="flex-1">{children}</div>
      {onClose ? (
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground text-xs shrink-0"
        >
          Dismiss
        </button>
      ) : null}
    </div>
  );
}
